/**
 * Wiring for the search results page's `previous_orders=true` mode (KAN-51):
 * turning the customer's previously-ordered product ids into a Typesense
 * filter, telling whether that filter is actually reflected in the results on
 * screen, and restoring the backend's ordering on the hits that come back.
 *
 * Pure functions, deliberately kept out of the component so the rules that are
 * easy to get wrong are testable without an InstantSearch harness.
 */

import type { TypesenseProduct } from "@/lib/typesense";

/**
 * The id set as a raw Typesense `filter_by` clause, for `Configure`'s
 * `filters` prop.
 *
 * **This must not go through `facetFilters`.** That was the original approach
 * and it silently dropped the id filter whenever any sidebar refinement was
 * active. `facetFilters` is not one of algoliasearch-helper's
 * `managedParameters`, so a Configure-supplied value survives into
 * `getQueryParams()` and is then deep-merged with the widget-derived one
 * (`merge({}, state.getQueryParams(), additionalParams)` in `requestBuilder`).
 * That merge recurses into arrays and combines them *index-wise* rather than
 * concatenating, so `[["id:a","id:b"]]` and `[["brand.name:L'Oreal"]]` become
 * `[["brand.name:L'Oreal","id:b"]]` - a single OR group spanning two fields.
 * typesense-instantsearch-adapter then logs "does not support cross-field ORs"
 * and emits only the first field's clause, so every product of that brand in
 * the catalogue renders under the "Previous orders" heading.
 *
 * `filters` has neither problem: it is a string, so the merge replaces rather
 * than recurses, and nothing else writes it (`requestBuilder` only ever sets
 * `facets`, `tagFilters`, `facetFilters` and `numericFilters`). The adapter
 * ANDs it with the widget-derived clause - `_adaptFilters` pushes
 * `params.filters` and the adapted `facetFilters` into one array joined with
 * `" && "` - which is the composition the sidebar filters need.
 *
 * `:=` is exact match. `id` needs no schema entry to be filterable: the
 * Typesense server special-cases it ahead of the collection schema (api#210).
 * Ids are Medusa `prod_<ULID>` values - alphanumeric and `_` only - so they
 * need no escaping inside the bracket list.
 *
 * Returns `undefined` for an empty list so the caller can decline to render
 * the filter at all rather than sending `id:=[]`.
 */
export function buildPreviousOrdersFilter(
  productIds: string[]
): string | undefined {
  if (productIds.length === 0) return undefined;
  return `id:=[${productIds.join(",")}]`;
}

/**
 * Whether the results currently on screen were produced by the id filter.
 *
 * The mode cannot use "has the ids request settled?" for this. The dropdown
 * prefetches the same query key on focus, so by the time the results page
 * mounts the ids are already cached and react-query reports `isPending: false`
 * on the very first render - while InstantSearch is still holding the results
 * of whatever search came before. Rendering then shows the previous, unfiltered
 * hits and a catalogue-sized count under the "Previous orders" heading.
 *
 * So the question is answered from the results themselves, using the invariant
 * the filter guarantees: every hit it returns is in the id set. Stale results
 * from an ordinary search will contain something outside it, and `hitsPerPage`
 * - which the adapter echoes back from the request's `per_page` - will not
 * match the single-page size this mode asks for.
 *
 * Both checks are needed. `hitsPerPage` alone would accept a coincidence
 * (a prior search whose page size happened to match); membership alone would
 * accept a stale *empty* result set, since a vacuous `every` is `true`.
 */
export function previousOrdersFilterHasLanded(
  results: { hitsPerPage?: number } | undefined,
  hits: Array<Pick<TypesenseProduct, "id">>,
  productIds: string[]
): boolean {
  if (!results) return false;
  if (results.hitsPerPage !== productIds.length) return false;
  const ids = new Set(productIds);
  return hits.every((hit) => ids.has(hit.id));
}

/**
 * Re-order hits into the backend's most-recently-ordered-first sequence.
 *
 * Typesense has no way to sort by "when did *this customer* last buy it" - the
 * index is shared across every salon - and without a text query there is no
 * relevance ranking to fall back on either, so the returned order would
 * otherwise be the index's internal one. The endpoint already returns the ids
 * in the right sequence, so the ordering is restored here against that list.
 *
 * This is also why the mode requests its whole result set as one page: sorting
 * page 1 of N locally would order each page internally while leaving products
 * on the wrong page, which is worse than not sorting at all.
 *
 * Hits missing from `productIds` keep their relative order and sort last, so a
 * result the filter somehow admitted is never silently dropped.
 */
export function orderHitsByProductIds<T extends Pick<TypesenseProduct, "id">>(
  hits: T[],
  productIds: string[]
): T[] {
  const rank = new Map(productIds.map((id, index) => [id, index]));
  return [...hits].sort(
    (a, b) =>
      (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER)
  );
}
